import { useSelector } from "react-redux";
import { Grid, Input, Slider, Typography, Select, MenuItem, FormControl, Divider, ToggleButtonGroup, ToggleButton } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { selectEconomics } from "../reducers/economicsSlice";
import { useCallback } from "react";

import { E3M3_TO_MCF, M3_TO_BBL } from "../helpers/conversionFactors";

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    margin: '10px 0',
    padding: '10px',
    backgroundColor: 'transparent',
  },
  input: {
    width: 70,
  },
}));

export const EconomicsSetting = ({ cadToUsd, parameter, handlePriceDeckChange, economicsSettings, setEconomicsSettings }) => {

  const classes = useStyles();

  const economics = useSelector(selectEconomics);

  const currency = economicsSettings?.currency;

  const handleCommodityChange = useCallback((parameter, value) => {
    try {
      let newSettings = {...economicsSettings};
      const priceDeckName = newSettings?.priceDeckName;

      newSettings[parameter] = economics?.prices?.[priceDeckName]?.map(p => {
        return {
          date: p.date,
          price: p[value],
          commodity: value,
          cadToUsd: p.cadToUsd || economics?.prices?.[priceDeckName]?.slice(-1)?.[0]?.cadToUsd || 0.75,
        };
      });
      
      setEconomicsSettings(newSettings);

    } catch (error) {
      return;
    };
  }, [economics?.prices, economicsSettings, setEconomicsSettings]);
  
  const handleInputChange = async (val, parameter) => {
    try {
      let value = val;
      let newSettings = {...economicsSettings};

      if (
        parameter === 'royalty' || 
        parameter === 'taxRate' || 
        parameter === 'localTaxRate' || 
        parameter === 'adValorem' || 
        parameter === 'priceEscalation' || 
        parameter === 'costEscalation' || 
        parameter === 'ownership' || 
        parameter === 'ownershipCost' || 
        parameter === 'ownershipCapex' || 
        parameter === 'nglGasShrinkage' ||
        parameter === 'discountRate'
      ) {
        value = Number(value) / 100;
        newSettings[parameter] = value;
      } else if (parameter === 'capex' || parameter === 'abandonment') {
        value = Number(value) * 1000000 / (currency === 'usd' ? cadToUsd : 1);
        newSettings[parameter] = value;
      } else if (
        parameter === 'tangibleCapital' || 
        parameter === 'intangibleCapital' ||
        parameter === 'salvageValue' ||
        parameter === 'ggDeduction'
      ) {
        value = Number(value) * 1000 / (currency === 'usd' ? cadToUsd : 1);
        newSettings[parameter] = value;
      } else if ((parameter === 'opexVariableOil' || parameter === 'opexVariableCondensate' || parameter === 'opexVariableWater') && currency === 'usd') {
        value = Number(value) * M3_TO_BBL / (currency === 'usd' ? cadToUsd : 1);
        newSettings[parameter] = value;
      } else if (parameter === 'opexVariableGas' && currency === 'usd') {
        value = Number(value) * E3M3_TO_MCF / (currency === 'usd' ? cadToUsd : 1);
        newSettings[parameter] = value;
      } else if (
        parameter === 'opexFixed' || 
        parameter === 'priceOffsetOil' || 
        parameter === 'priceOffsetGas' || 
        parameter === 'priceOffsetPropane' || 
        parameter === 'priceOffsetButane' ||
        parameter === 'priceOffsetPentanes' ||
        parameter === 'priceOffsetCondensate'
        ) {
        value = Number(value) / (currency === 'usd' ? cadToUsd : 1);
        newSettings[parameter] = value;
      } else if (parameter === 'country') {
        if (value === 'canada') {
          newSettings.provinceOrState = 'alberta';
        } else if (value === 'usa') {
          newSettings.provinceOrState = 'newMexico';
        };
        newSettings[parameter] = value;
      } else if (parameter === 'provinceOrState') {
        newSettings[parameter] = value;
      } else if (parameter === 'abandonmentDelay') {
        newSettings[parameter] = Number(value);
      } else {
        newSettings[parameter] = Number(value);
      };

      setEconomicsSettings(newSettings);

    } catch (error) {
      console.log('Could not update economics parameter');
      console.log(error);
      return;
    };
  };

  const value = economicsSettings?.[parameter];
  const commodities = getCommodities(economics, economicsSettings?.priceDeckName, parameter);

  return (
    <div className={classes.root}>
      <Typography id='input-slider' gutterBottom noWrap>
        {getLabelName(parameter)} <span style={{fontSize: 12}}>{getUnits(parameter, currency)}</span>
      </Typography>

      {renderInput()}
    </div>
  );

  // FUNCTIONS
  function renderInput() {
    try {
      switch(parameter) {
        case 'priceOil': 
        case 'priceGas': 
        case 'priceCondensate': 
        case 'pricePropane': 
        case 'priceButane': 
        case 'pricePentanes': 
          return (
            <FormControl variant="filled" style={{width: '100%'}}>
              <Select 
                value={economicsSettings?.[parameter]?.[0]?.commodity || ""}
                label="Commodity"
                onChange={(e) => handleCommodityChange(parameter, e.target.value)}
                style={{width: '100%'}}
              >
                <Typography color="textSecondary" style={{margin: 10}}>{economicsSettings?.currency === 'usd' ? 'USD' : 'CAD'}</Typography>
                {commodities?.map(c => {
                  return (
                    <MenuItem value={c} key={c}>{c}</MenuItem>
                  );
                })}
              </Select>
              <Typography color="textSecondary" variant="subtitle2" style={{fontSize: 12}}>{`Latest price: ${getCurrentPrice(parameter)} ${getUnits(parameter, currency)}`}</Typography>
            </FormControl>
          );

        case 'priceDeckName':
          return (
            <FormControl variant="filled" style={{width: '100%'}}>
              <Select 
                value={value}
                label="Price Deck"
                onChange={(e) => handlePriceDeckChange(e.target.value)}
                style={{width: '100%'}}
              >
                {economics?.prices && Object.keys(economics?.prices)?.map(c => {
                  return (
                    <MenuItem value={c} key={c}>{c}</MenuItem>
                  );
                })}
                <Divider />
              </Select>
            </FormControl> 
          );

        case 'cutoffMethod':
          return (
            <FormControl component="fieldset" style={{width: '100%', padding: '0 10px', marginTop: 10}}>
              <ToggleButtonGroup
                value={value}
                exclusive
                onChange={(e, newValue) => handleInputChange(newValue, parameter)}
                size="small"
                style={{height: 30, width: '100%', display: 'flex', justifyContent: 'center', marginBottom: 20, marginTop: 10}}
              >
                <ToggleButton value={1} size="small" style={{width: '50%'}} color="primary">
                  <Typography style={{fontSize: 12}}>
                    Individual
                  </Typography>
                </ToggleButton>
                <ToggleButton value={2} size="small" style={{width: '50%'}} color="primary">
                  <Typography style={{fontSize: 12}}>
                    Common
                  </Typography>
                </ToggleButton>
              </ToggleButtonGroup>
            </FormControl>
          );
        

        case 'country':
          return (
            <FormControl variant="filled" style={{width: '100%'}}>
              <Select 
                value={value || 'canada'}
                label="Country"
                onChange={(e) => handleInputChange(e.target.value, parameter)}
                style={{width: '100%'}}
              >
                <MenuItem value={'canada'}>Canada</MenuItem>
                <MenuItem value={'usa'}>USA</MenuItem>
              </Select>
            </FormControl> 
          );

        case 'provinceOrState':
          return (
            <FormControl variant="filled" style={{width: '100%'}}>
              <Select 
                value={value || 'alberta'}
                label="Province/State"
                onChange={(e) => handleInputChange(e.target.value, parameter)}
                style={{width: '100%'}}
              >
                <MenuItem value={(!economicsSettings?.country || economicsSettings?.country === 'canada') ? 'alberta' : 'newMexico'}>{(!economicsSettings?.country || economicsSettings?.country === 'canada') ? 'Alberta' : 'New Mexico'}</MenuItem>
              </Select>
            </FormControl> 
          );

        default:
          return (
            <Grid container spacing={2} alignItems="center">
              <Grid item xs style={{margin: '0 10px'}}>
                <Slider
                  value={formatInput(parameter, value, currency, cadToUsd) || 0}
                  onChange={(e, newValue) => handleInputChange(newValue, parameter)}
                  min={getMinMaxStep(parameter, value)?.[0]}
                  max={getMinMaxStep(parameter, value)?.[1]}
                  step={getMinMaxStep(parameter, value)?.[2]}
                  valueLabelDisplay="auto"
                />
              </Grid>
              <Grid item>
                <Input
                  className={classes.input}
                  value={formatInput(parameter, value, currency, cadToUsd) || 0}
                  margin="dense"
                  onChange={(e) => handleInputChange(e.target.value, parameter)}
                  inputProps={{
                    min: getMinMaxStep(parameter, value)?.[0],
                    max: getMinMaxStep(parameter, value)?.[1],
                    step: getMinMaxStep(parameter, value)?.[2],
                    type: 'number',
                  }}
                />
              </Grid>
            </Grid>
          );
      }  
    } catch (error) {
      console.log(error);
      return <></>;
    };
  };


  function getCurrentPrice(param) {
    const currency = economicsSettings?.currency;
    const commodityPrice = economicsSettings?.[param]?.find(p => new Date(p.date)?.getFullYear() >= new Date()?.getFullYear());
    return (commodityPrice?.price * (currency === 'usd' ? commodityPrice?.cadToUsd : 1) || 0)?.toFixed(2);
  };

  function getMinMaxStep(param, value) {
    switch(param) {
      case 'priceOil':
        return [0.1, 100, 0.01];
      case 'priceGas':
        return [0.1, 20, 0.01];
      case 'priceCondensate':
        return [0.1, 100, 0.01];
      case 'pricePropane':
        return [0.1, 100, 0.01];
      case 'priceButane':
        return [0.1, 100, 0.01];
      case 'pricePentanes':
        return [0.1, 100, 0.01];
      case 'mcfToMmbtu':
        return [1, 1.3, 0.01];
      case 'opexVariableOil':
        return [0, 100, 0.1];
      case 'opexVariableGas':
        return [0, 100, 0.1];
      case 'opexVariableCondensate':
        return [0, 100, 0.1];
      case 'opexVariableWater':
        return [0, 100, 0.1];
      case 'opexFixed':
        return [0, 10000, 0.1];
      case 'capex':
        return [0, 10, 0.01];
      case 'abandonment':
        return [0, 10, 0.01];
      case 'abandonmentDelay':
        return [0, 120, 1];
      case 'royalty':
        return [0, 100, 0.1];
      case 'adValorem':
        return [0, 100, 0.1];
      case 'priceEscalation':
        return [0, 100, 0.1];
      case 'costEscalation':
        return [0, 100, 0.1];
      case 'ownership':
        return [0, 100, 0.1];
      case 'ownershipCost':
        return [0, 100, 0.1];
      case 'ownershipCapex':
        return [0, 100, 0.1];
      case 'priceOffsetOil':
        return [-100, 100, 0.1];
      case 'priceOffsetGas':
        return [-10, 10, 0.1];
      case 'priceOffsetCondensate':
        return [-100, 100, 0.1];
      case 'priceOffsetPropane':
        return [-100, 100, 0.1];
      case 'priceOffsetButane':
        return [-100, 100, 0.1];
      case 'priceOffsetPentanes':
        return [-100, 100, 0.1];
      case 'nglGasShrinkage':
        return [0, 100, 0.1];
      case 'nglPropaneYield':
        return [0, 100, 0.1];
      case 'nglButaneYield':
        return [0, 100, 0.1];
      case 'nglPentanesYield':
        return [0, 100, 0.1];
      case 'discountRate':
        return [5, 50, 5];
      case 'tangibleCapital':
        return [0, 5000, 1];
      case 'intangibleCapital':
        return [0, 5000, 1];
      case 'salvageValue':
        return [0, 5000, 1];
      case 'ggDeduction':
        return [0, 5000, 1];
      case 'taxRate':
        return [0, 100, 0.1];
      case 'localTaxRate':
        return [0, 100, 0.1];
      default:
        return [value ? Math.pow(10, Math.floor(Math.log10(value))) : "", value ? Math.pow(10, Math.ceil(Math.log10(value))) : "", 0.1];
    };
  };

  function formatInput(param, value, currency, cadToUsd) {
    switch(param) {
      case 'royalty':
      case 'adValorem':
      case 'priceEscalation':
      case 'costEscalation':
      case 'ownership':
      case 'ownershipCost':
      case 'ownershipCapex':
      case 'nglGasShrinkage':
        return Math.round((value * 100 + Number.EPSILON) * 100) / 100;
      case 'opexVariableOil':
        if (currency === 'usd') {
          return Math.round((value / M3_TO_BBL * cadToUsd + Number.EPSILON) * 100) / 100;
        } else {
          return Math.round((value + Number.EPSILON) * 100) / 100;
        };
      case 'opexVariableGas':
        if (currency === 'usd') {
          return Math.round((value / E3M3_TO_MCF * cadToUsd + Number.EPSILON) * 100) / 100;
        } else {
          return Math.round((value + Number.EPSILON) * 100) / 100;
        };
      case 'opexVariableCondensate':
        if (currency === 'usd') {
          return Math.round((value / M3_TO_BBL * cadToUsd + Number.EPSILON) * 100) / 100;
        } else {
          return Math.round((value + Number.EPSILON) * 100) / 100;
        };
      case 'opexVariableWater':
        if (currency === 'usd') {
          return Math.round((value / M3_TO_BBL * cadToUsd + Number.EPSILON) * 100) / 100;
        } else {
          return Math.round((value + Number.EPSILON) * 100) / 100;
        };
      case 'opexFixed':
      case 'priceOffsetOil':
      case 'priceOffsetGas':
      case 'priceOffsetCondensate':
      case 'priceOffsetPropane':
      case 'priceOffsetButane':
      case 'priceOffsetPentanes':
        return Math.round(((value * (currency === 'usd' ? cadToUsd : 1)) + Number.EPSILON) * 100) / 100;
      case 'capex':
      case 'abandonment':
        return Math.round(((value * (currency === 'usd' ? cadToUsd : 1) / 1000000) + Number.EPSILON) * 100) / 100;
      case 'abandonmentDelay':
        return value;
      case 'tangibleCapital':
      case 'intangibleCapital':
      case 'salvageValue':
      case 'ggDeduction':
        return Math.round(((value * (currency === 'usd' ? cadToUsd : 1) / 1000) + Number.EPSILON) * 100) / 100;
      case 'discountRate':
        return Math.round((value * 100 + Number.EPSILON) * 100) / 100;
      case 'cutoffMethod':
        return value;
      case 'taxRate':
        return Math.round((value * 100 + Number.EPSILON) * 100) / 100;
      case 'localTaxRate':
        return Math.round((value * 100 + Number.EPSILON) * 100) / 100;
      default:
        return Math.round((value + Number.EPSILON) * 100) / 100;
    }
  };

  
  function getLabelName(parameter) {
    switch(parameter) {
      case 'priceDeckName':
        return 'Price Deck';
      case 'priceOil':
        return 'Oil Price';
      case 'priceGas':
        return 'Gas Price';
      case 'priceCondensate':
        return 'Condensate Price';
      case 'pricePropane':
        return 'Propane Price';
      case 'priceButane':
        return 'Butane Price';
      case 'pricePentanes':
        return 'Pentanes+ Price';
      case 'mcfToMmbtu':
        return 'MCF to MMBTU';
      case 'exchangeRate':
        return 'Exchange Rate';
      case 'royalty':
        return 'Royalty';
      case 'opexVariableOil':
        return 'Oil Opex';
      case 'opexVariableGas':
        return 'Gas Opex';
      case 'opexVariableCondensate':
        return 'Condensate Opex';
      case 'opexVariableWater':
        return 'Water Opex';
      case 'opexFixed':
        return 'Fixed Opex';
      case 'adValorem':
        return 'Ad Valorem Tax Rate';
      case 'discountFactor':
        return 'Discount Factor';
      case 'capex':
        return 'Capex';
      case 'priceEscalation':
        return 'Price Escalation';
      case 'costEscalation':
        return 'Cost Escalation';
      case 'abandonment':
        return 'Abandonment';
      case 'abandonmentDelay':
        return 'Abandonment Delay';
      case 'ownership':
        return 'Working Interest';
      case 'ownershipCost':
        return 'Cost Interest';
      case 'ownershipCapex':
        return 'Capital Interest';
      case 'discountRate':
        return 'Cutoff NPV';
      case 'priceOffsetOil':
        return 'Oil Price Offset';
      case 'priceOffsetGas':
        return 'Gas Price Offset';
      case 'priceOffsetCondensate':
        return 'Condensate Price Offset';
      case 'priceOffsetPropane':
        return 'Propane Price Offset';
      case 'priceOffsetButane':
        return 'Butane Price Offset';
      case 'priceOffsetPentanes':
        return 'Pentanes+ Price Offset';
      case 'nglGasShrinkage':
        return 'Gas Shrinkage';
      case 'nglPropaneYield':
        return 'Raw Gas Propane Yield';
      case 'nglButaneYield':
        return 'Raw Gas Butane Yield';
      case 'nglPentanesYield':
        return 'Raw Gas Pentanes+ Yield';
      case 'cutoffMethod':
        return 'Termination Method';
      case 'country':
        return 'Country';
      case 'provinceOrState':
        return 'Province/State';
      case 'tangibleCapital':
        return 'Tangible Capital';
      case 'intangibleCapital':
        return 'Intangible Capital';
      case 'salvageValue':
        return 'Salvage Value';
      case 'ggDeduction':
        return 'G&G Deduction';
      case 'taxRate':
        return 'Federal Tax Rate';
      case 'localTaxRate':
        return 'Local Tax Rate';
      default:
        return '';
    };
  };
  
  function getUnits(parameter, currency) {
    switch(parameter) {
      case 'priceOil':
        return currency === 'cad' ? 'CAD/bbl' : 'USD/bbl';
      case 'priceGas':
        return currency === 'cad' ? 'CAD/MMbtu' : 'USD/MMbtu';
      case 'priceCondensate':
        return currency === 'cad' ? 'CAD/bbl' : 'USD/bbl';
      case 'pricePropane':
        return currency === 'cad' ? 'CAD/bbl' : 'USD/bbl';
      case 'priceButane':
        return currency === 'cad' ? 'CAD/bbl' : 'USD/bbl';
      case 'pricePentanes':
        return currency === 'cad' ? 'CAD/bbl' : 'USD/bbl';
      case 'royalty':
        return '%';
      case 'adValorem':
        return '%';
      case 'opexVariableOil':
        return currency === 'cad' ? 'CAD/m3/well' : 'USD/bbl/well';
      case 'opexVariableGas':
        return currency === 'cad' ? 'CAD/E3m3/well' : 'USD/mcf/well';
      case 'opexVariableCondensate':
        return currency === 'cad' ? 'CAD/m3/well' : 'USD/bbl/well';
      case 'opexVariableWater':
        return currency === 'cad' ? 'CAD/m3/well' : 'USD/bbl/well';
      case 'opexFixed':
        return currency === 'cad' ? 'CAD/well/mo' : 'USD/well/mo';
      case 'capex':
        return currency === 'cad' ? 'MM CAD/well' : 'MM USD/well';
      case 'abandonment':
        return currency === 'cad' ? 'MM CAD/well' : 'MM USD/well';
      case 'abandonmentDelay':
        return 'months';
      case 'priceEscalation':
        return '%';
      case 'costEscalation':
        return '%';
      case 'ownership':
        return '%';
      case 'ownershipCost':
        return '%';
      case 'ownershipCapex':
        return '%';
      case 'cutoff':
        return `${currency === 'cad' ? 'CAD' : 'USD'}/mo`;
      case 'priceOffsetOil':
        return `${currency === 'cad' ? 'CAD' : 'USD'}`;
      case 'priceOffsetGas':
        return `${currency === 'cad' ? 'CAD' : 'USD'}`;
      case 'priceOffsetCondensate':
        return `${currency === 'cad' ? 'CAD' : 'USD'}`;
      case 'priceOffsetPropane':
        return `${currency === 'cad' ? 'CAD' : 'USD'}`;
      case 'priceOffsetButane':
        return `${currency === 'cad' ? 'CAD' : 'USD'}`;
      case 'priceOffsetPentanes':
        return `${currency === 'cad' ? 'CAD' : 'USD'}`;
      case 'nglGasShrinkage':
        return '%';
      case 'nglPropaneYield':
        return 'bbl/mmscf';
      case 'nglButaneYield':
        return 'bbl/mmscf';
      case 'nglPentanesYield':
        return 'bbl/mmscf';
      case 'discountRate':
        return '%';
      case 'taxRate':
        return '%';
      case 'localTaxRate':
        return '%';
      case 'tangibleCapital':
        return `${currency === 'cad' ? 'M CAD' : 'M USD'}`;
      case 'intangibleCapital':
        return `${currency === 'cad' ? 'M CAD' : 'M USD'}`;
      case 'salvageValue':
        return `${currency === 'cad' ? 'M CAD' : 'M USD'}`;
      case 'ggDeduction':
        return `${currency === 'cad' ? 'M CAD' : 'M USD'}`;
      default:
        return '';
    };
  };
  
  function getCommodities(economics, priceDeckName, parameter) {
    try {
      switch(parameter) {
        case 'priceOil':
          if (priceDeckName?.toLowerCase()?.includes('consultants')) { // if price deck from McDaniel
            return [
              'WTI Crude Oil (CAD/bbl)',
              'Brent Crude Oil (CAD/bbl)',
              'Edmonton Light Crude Oil (CAD/bbl)',
              'Alberta Bow River Hardisty (CAD/bbl)',
              'Western Canadian Select (CAD/bbl)',
              'Alberta Heavy Crude Oil (CAD/bbl)',
              'Sask Cromer Medium Crude Oil (CAD/bbl)'
            ];
          };
          break;
        
        case 'priceGas':
          if (priceDeckName?.toLowerCase()?.includes('consultants')) { // if price deck from McDaniel
            return [
              'US Henry Hub (CAD/MMBtu)',
              'Alberta AECO (CAD/MMBtu)',
              'Alberta Average (CAD/MMBtu)',
              'Alberta Aggregator (CAD/MMBtu)',
              'Empress (CAD/MMBtu)',
              'Sask. Prov. Gas. (CAD/MMBtu)',
              'BC Average (CAD/MMBtu)',
              'BC Station 2 (CAD/MMBtu)'
            ];
          };
          break;

        case 'priceCondensate':
          if (priceDeckName?.toLowerCase()?.includes('consultants')) { // if price deck from McDaniel
            return [
              'Edmonton Cond. And Nat. Gasolines (CAD/bbl)'
            ];
          };
          break;

        case 'pricePropane':
          if (priceDeckName?.toLowerCase()?.includes('consultants')) { // if price deck from McDaniel
            return [
              'Edmonton Propane (CAD/bbl)'
            ];
          };
          break;

        case 'priceButane':
          if (priceDeckName?.toLowerCase()?.includes('consultants')) { // if price deck from McDaniel
            return [
              'Edmonton Butane (CAD/bbl)'
            ];
          };
          break;

        case 'pricePentanes':
          if (priceDeckName?.toLowerCase()?.includes('consultants')) { // if price deck from McDaniel
            return [
              'Edmonton Cond. And Nat. Gasolines (CAD/bbl)'
            ];
          };
          break;

        default:
          return [];
      };

      return commodities;
    } catch (error) {
      return null;
    };
  };
};